import { useMutation } from "@apollo/client";
import Print from "@mui/icons-material/Print";
import { Box, Button, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GetWasteContainerQuery, LabelExportFormat, LabelFormat } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { downloadFromUrl } from "util/download";

import { EXPORT_CONTAINER_LABELS_MUTATION } from "../api";
import { SectionCard } from "../containerSections";
import { LabelPreviewStage } from "./LabelPreviewStage";

/** The fields this section needs off the container detail query -- a subset
 * of `WasteContainerDetail`'s full row, so its test fixtures don't have to
 * carry fields (capacity, clock, fill events, ...) it never reads. */
export type ContainerLabelSectionContainer = Pick<
  NonNullable<GetWasteContainerQuery["wasteContainer"]>,
  "id" | "containerLabel" | "containerType" | "capacity" | "capacityUnit" | "label" | "labelNote"
>;

function labelScreenPath(tenantId: string | undefined, containerId: string): string {
  return `/o/${tenantId}/waste/containers/${containerId}/label`;
}

/** Zoom bounds for this section's inline preview, which gets half the card's
 * width (see the stage's wrapping Box below) rather than the full-page stage
 * the defaults assume. The ceiling keeps the 4x6 label thumbnail-sized (the
 * default would blow it up to ~1150px tall); the floor sits below the global
 * legibility floor so the fit can keep shrinking the label into that
 * half-width stage instead of overflowing it into a horizontal scroll. The
 * label screen stays the place to inspect the artifact at full size. */
const SECTION_PREVIEW_MAX_ZOOM = 0.3;
const SECTION_PREVIEW_MIN_ZOOM = 0.2;

export function ContainerLabelSection({
  container,
}: {
  container: ContainerLabelSectionContainer;
}): ReactElement {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const alerts = useAlerts();
  const [exportLabels, { loading: exporting }] = useMutation(EXPORT_CONTAINER_LABELS_MUTATION);

  const goToLabelScreen = () => navigate(labelScreenPath(tenantId, container.id));

  // Unlike the label screen's Download (which can reuse whatever format the
  // reader last picked), this section has no format controls of its own --
  // it always renders the branded, full 4x6 label, the same defaults the
  // label screen starts with.
  const handleDownload = () => {
    exportLabels({
      variables: {
        containerIds: [container.id],
        layout: LabelFormat.Full,
        fileType: LabelExportFormat.Pdf,
        branded: true,
      },
      onCompleted: (result) => {
        void downloadFromUrl(
          result.exportContainerLabels.url,
          `container-label-${container.containerLabel}.pdf`
        );
      },
      onError: (mutationError) => {
        alerts.error(mutationError.message || "Failed to download the label.");
      },
    });
  };

  return (
    <SectionCard title="Label, placard & QR">
      {/* Actions beside the preview, not under it -- the buttons ride along
          the artifact's height instead of adding a row of their own, keeping
          this card level with the classification card sharing its grid row. */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
        <Box sx={{ minWidth: 0, width: { xs: "100%", sm: "50%" } }}>
          <LabelPreviewStage
            content={container.label}
            layout={LabelFormat.Full}
            branded={true}
            size="sm"
            maxZoom={SECTION_PREVIEW_MAX_ZOOM}
            minZoom={SECTION_PREVIEW_MIN_ZOOM}
            stagePadding={1.5}
            containerType={container.containerType}
            capacity={container.capacity}
            capacityUnit={container.capacityUnit}
          />
        </Box>
        {/* flex: 1 + alignItems so the buttons sit centered in the column of
            space beside the preview, not tucked into its top-left corner. */}
        <Stack spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Button variant="outlined" size="small" startIcon={<Print />} onClick={goToLabelScreen}>
            Print
          </Button>
          <Button variant="outlined" size="small" loading={exporting} onClick={handleDownload}>
            Download
          </Button>
        </Stack>
      </Stack>
    </SectionCard>
  );
}
